import { useEffect, useState } from 'react';

// We can't use e.g. `typed.js` because that modifies the DOM element's `innerHTML`
// directly, which breaks our Markdown -> JSX conversion.
// I'm also not sure how well that'd interact with the changing `targetText` from the AI.
// Lastly, this custom solution lets us add `patternsToInstantlyType`.
export const useTyper = ({
  targetText,
  skip,
  msPerChar,
  patternsToInstantlyType,
}: {
  readonly targetText: string;
  readonly skip?: boolean;
  readonly msPerChar: number;
  readonly patternsToInstantlyType?: readonly RegExp[];
}) => {
  const [typedText, setTypedText] = useState('');

  useEffect(() => {
    if (!targetText || skip) {
      return;
    }

    const interval = setInterval(() => {
      setTypedText((prev) => {
        if (!targetText.startsWith(prev)) {
          // Target text has changed entirely
          clearInterval(interval);
          return '';
        } else if (prev.length >= targetText.length) {
          // Done typing
          clearInterval(interval);
          return prev;
        }

        for (const regex of patternsToInstantlyType ?? []) {
          const match = targetText.slice(prev.length).match(regex);
          if (match) {
            // Instantly type the entire match so user doesn't see the raw text
            return prev + match[0];
          }
        }

        const nextChar = targetText[prev.length];
        return prev + nextChar;
      });
    }, msPerChar);

    return () => clearInterval(interval);
    // Intentionally exclude `patternsToInstantlyType` - React would always consider it changed.
    // Presumably the caller will not pass a dynamic value anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetText, msPerChar, skip]);

  return skip ? targetText : typedText;
};
